import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import omit from "lodash/omit";
import { showLoader, hideLoader } from "store/misc/loaderSlice";
import { showMessage } from "store/misc/messageSlice";
import { clearCart } from "store/vino/cartSlice";
import { dispatchClearCart } from "eventBus";
import { resetDeliveryMethods } from "./deliveryMethodsSlice";
import * as models from "vino/components/cart-checkout/models";
import checkoutService from "services/checkoutService";
import customerService from "services/customerService";

export const createCheckout = createAsyncThunk(
	"vino/checkout/create",
	async (model, { dispatch, rejectWithValue, getState }) => {
		try {
			dispatch(setLoading(true));
			dispatch(setSubmitting(true));
			dispatch(showLoader());
			const { auth } = getState();
			const { isUserLoaded } = auth.user;
			let customerId;
			if (!isUserLoaded) {
				customerId = await customerService.create(model);
			}

			const {
				id,
				shippingAddressId,
				checkoutLineItems,
			} = await checkoutService.create(model, customerId);
			const { customer } = cloneDeep(model);
			customer.address.id = shippingAddressId;
			customer.id = customerId;
			return {
				id,
				customer,
				checkoutLineItems,
			};
		} catch (error) {
			console.error(error);
			dispatch(
				showMessage({
					message: "Error occurred while saving",
					variant: "error",
				})
			);
			return rejectWithValue(error);
		} finally {
			dispatch(setSubmitting(false));
			dispatch(hideLoader());
			dispatch(setLoading(false));
		}
	}
);

export const getCheckout = createAsyncThunk(
	"vino/checkout/fetch",
	async (checkoutId, { dispatch }) => {
		try {
			dispatch(showLoader());
			dispatch(setLoading(true));
			const checkout = await checkoutService.fetch(checkoutId);
			return checkout;
		} catch (error) {
			console.error(error);
			dispatch(
				showMessage({
					message: "Error occurred while fetching",
					variant: "error",
				})
			);
		} finally {
			dispatch(hideLoader());
			dispatch(setLoading(false));
		}
	}
);

export const updateCheckout = createAsyncThunk(
	"vino/checkout/update",
	async (checkout, { dispatch, rejectWithValue }) => {
		dispatch(showLoader());
		dispatch(setSubmitting(true));
		try {
			await checkoutService.update(checkout);
			return checkout;
		} catch (error) {
			console.error(error);
			dispatch(
				showMessage({
					message: "Error occurred while saving",
					variant: "error",
				})
			);
			return rejectWithValue(error);
		} finally {
			dispatch(hideLoader());
			dispatch(setSubmitting(false));
		}
	}
);

export const updateTaxRates = (salesTaxRates) => (dispatch, getState) => {
	const { recommendedSalesTaxDue, shipmentSalesTaxRates } = salesTaxRates;
	dispatch(setTotalTax(recommendedSalesTaxDue));
	const { checkout } = getState().vino;

	const lineItems = checkout.checkoutLineItems;
	if (shipmentSalesTaxRates.length > 0) {
		const { productSalesTaxRates } = shipmentSalesTaxRates[0];
		const updatedLineItems = lineItems.map(({ id, variant }) => {
			const taxRate = find(productSalesTaxRates, { productKey: variant.sku });
			return {
				id,
				tax: taxRate.salesTaxDue,
				taxRate: taxRate.salesTaxRate,
				variant,
			};
		});
		dispatch(setLineItems(updatedLineItems));
	}
};

export const updateShippingRate = createAsyncThunk(
	"vino/checkout/updateShippingRate",
	async (checkout, { dispatch, rejectWithValue }) => {
		dispatch(showLoader());
		dispatch(setSubmitting(true));
		try {
			const { shippingRate } = await checkoutService.createShippingRate(
				checkout
			);
			return shippingRate;
		} catch (error) {
			console.error(error);
			dispatch(
				showMessage({
					message: "Error occurred while saving",
					variant: "error",
				})
			);
			return rejectWithValue(error);
		} finally {
			dispatch(hideLoader());
			dispatch(setSubmitting(false));
		}
	}
);

export const updatePaymentMethod = createAsyncThunk(
	"vino/checkout/updatePaymentMethod",
	async ({ model, navigate }, { dispatch, rejectWithValue }) => {
		dispatch(showLoader());
		dispatch(setSubmitting(true));
		try {
			await checkoutService.completedCheckout(model);
			dispatch(clearCart());
			dispatch(resetCheckout());
			dispatch(resetDeliveryMethods());
			dispatchClearCart();
			navigate(`/invoice/${model.checkout.checkoutId}`);
		} catch (error) {
			console.error(error);
			dispatch(
				showMessage({
					message: "Error occurred while placing order",
					variant: "error",
				})
			);
			rejectWithValue(error);
		} finally {
			dispatch(hideLoader());
			dispatch(setSubmitting(false));
		}
	}
);

const initialState = {
	checkoutId: null,
	isLoading: false,
	isSubmitting: false,
	subtotalPrice: 0,
	totalTax: 0,
	totalDiscounts: 0,
	step: 1,
	customer: {
		...models.contactDefaultValues,
	},
	shipping: {
		rate: 0,
	},
	payment: { ...models.paymentDefaultValues },
};

const checkoutSlice = createSlice({
	name: "vino/checkout",
	initialState,
	reducers: {
		resetCheckout: () => initialState,
		setCustomer: (state, action) => {
			state.customer = action.payload;
		},
		setAddress: (state, action) => {
			state.customer.address = omit(action.payload, [
				"__typename",
				"country",
				"state",
			]);
		},
		setShipping: (state, action) => {
			state.shipping = action.payload;
		},
		setPayment: (state, action) => {
			state.payment = action.payload;
		},
		setStep: (state, action) => {
			state.step = action.payload;
		},
		setSubmitting: (state, action) => {
			state.isSubmitting = action.payload;
		},
		setLoading: (state, action) => {
			state.isLoading = action.payload;
		},
		setSubtotalPrice: (state, action) => {
			state.subtotalPrice = action.payload;
		},
		setTotalTax: (state, action) => {
			state.totalTax = action.payload;
		},
		setTotalDiscounts: (state, action) => {
			state.totalDiscounts = action.payload;
		},
		setLineItems: (state, action) => {
			state.checkoutLineItems = action.payload;
		},
	},
	extraReducers: {
		[createCheckout.fulfilled]: (state, action) => {
			const { id, customer, checkoutLineItems } = action.payload;
			state.checkoutId = id;
			state.customer = customer;
			state.checkoutLineItems = checkoutLineItems;
			state.step = 2;
		},
		[updateCheckout.fulfilled]: (state, action) => {
			state.step = 2;
			state.customer = action.payload.customer;
		},
		[updateShippingRate.fulfilled]: (state, action) => {
			const { id } = action.payload;
			state.shipping.id = id;
			state.step = 3;
		},

		[getCheckout.fulfilled]: (state, action) => action.payload,
	},
});

export const {
	setCustomer,
	setAddress,
	setShipping,
	setPayment,
	setStep,
	setSubmitting,
	setLoading,
	resetCheckout,
	setSubtotalPrice,
	setTotalTax,
	setTotalDiscounts,
	setLineItems,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
